// TODO: remover esta regra
/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  useMemo,
  useCallback,
  useState,
  useRef,
  useEffect,
  Fragment,
} from 'react';

import { Box } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import clsx from 'clsx';

import { PaginationReturn } from 'generated';
import { strNormalize } from 'utils';

import {
  Head,
  Row,
  NoData,
  Pagination,
  PaginationAlignment,
  PaginationPosition,
} from './components';
import { getComparator, stableSort } from './functions';
import { useStyles } from './styles';
import {
  CompareFunction,
  DefineColumn,
  SearchParams,
  Order,
  SelectedRows,
} from './types';

export type RowType<T> = T & {
  id: string;
};

type GenericType = Record<string, any>;

interface DataTableProps<T = GenericType, C = GenericType> {
  data: T[];
  context?: C;
  loading?: boolean;
  rowSelectable?: boolean;
  pagination?: PaginationReturn;
  paginationPosition?: PaginationPosition;
  paginationAlignment?: PaginationAlignment;
  paginationDescription?: string;
  height?: string | number;
  hiddenColumns?: (keyof T | string)[];
  defineColumns: DefineColumn[];
  expansible?: boolean;
  onSelectRow?(row: T): void;
  dataTransform?(rows: T[]): any;
  onPagination?(nextPage: number): void;
  onSearch?(searchParams: SearchParams<T>): void;
  loadingRowsQuantity?: number;
  loadingColumnsQuantity?: number;
  selectedRows?: SelectedRows<T>;
  allExpanded?: boolean;
  limit: number;
  fixedHeader?: boolean;
}

const defaultTableHeight = '50vh';
const defaultHiddenColumns = ['id', 'type'];
const defaultLoadingSkeleton = 5;

function generateRowsSkeleton(quantity: number): number[] {
  return Array(quantity)
    .fill(0)
    .map((_, index) => index);
}

export function DataTable<T = any, C = any>({
  data,
  context,
  loading,
  rowSelectable,
  pagination,
  hiddenColumns,
  defineColumns,
  onPagination,
  onSelectRow,
  onSearch,
  expansible,
  dataTransform,
  loadingColumnsQuantity,
  selectedRows,
  allExpanded,
  paginationDescription,
  limit,
  fixedHeader = false,
  paginationPosition = 'bottom',
  paginationAlignment = 'right',
  loadingRowsQuantity = defaultLoadingSkeleton,
  height = defaultTableHeight,
}: DataTableProps<T, C>): JSX.Element {
  const classes = useStyles();
  const tableRef = useRef<HTMLDivElement | null>(null);

  const [rows, setRows] = useState<RowType<T>[]>(data as RowType<T>[]);
  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderby] = useState<keyof T | null>(null);
  const [columnCompareFunction, setColumnCompareFunction] =
    useState<CompareFunction | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(() => {
    if (pagination) {
      return pagination?.currentPage ?? 0;
    }

    return 0;
  });
  const [searchParams, setSearchParams] = useState<SearchParams<T>>(
    {} as SearchParams<T>,
  );
  const [expanded, setExpanded] = useState<Record<string, boolean>>(() => {
    if (rows.length === 1) {
      const [row] = rows;
      return { [row.id]: true };
    }

    return {};
  });

  const memoLoadingRowsQuantity = useMemo(() => {
    return generateRowsSkeleton(loadingRowsQuantity);
  }, [loadingRowsQuantity]);

  const memoDefineColumns = useMemo(() => {
    const hidden = hiddenColumns
      ? [...hiddenColumns, ...defaultHiddenColumns]
      : defaultHiddenColumns;

    return defineColumns.filter(column => !hidden.includes(column.id));
  }, [defineColumns, hiddenColumns]);

  const memoHasSearchParams = useMemo(() => {
    return !!Object.keys(searchParams || {}).length;
  }, [searchParams]);

  const handleColumnSort = useCallback(
    (property: keyof T, colCompareFunction?: CompareFunction) => {
      const isAsc = orderBy === property && order === 'asc';
      const compareFunction = colCompareFunction
        ? () => colCompareFunction
        : null;

      setColumnCompareFunction(compareFunction);
      setOrder(isAsc ? 'desc' : 'asc');
      setOrderby(property);
      setExpanded({});
      tableRef.current?.scrollTo(0, 0);
    },
    [order, orderBy],
  );

  const handleExpand = useCallback(
    (rowId: string) => {
      const exists = expanded[rowId];

      setExpanded(oldState => {
        const old = Object.entries<boolean>(oldState).reduce(
          (accumulator: Record<string, boolean>, [key]) => {
            accumulator[key] = false;

            return accumulator;
          },
          {} as any,
        );
        if (exists) {
          return { ...old, [rowId]: false };
        }

        return { [rowId]: true };
      });
    },
    [expanded],
  );

  const handleSelectRow = useCallback(
    (row: RowType<T>) => {
      if (rowSelectable && onSelectRow) {
        onSelectRow(row);
      }
    },
    [onSelectRow, rowSelectable],
  );

  const handleSearch = useCallback(
    (value: string, searchParam: keyof T) => {
      setExpanded({});
      let composeSearchParams: SearchParams<T> = {
        ...(searchParams ?? ({} as SearchParams<T>)),
        [searchParam]: strNormalize(value.toLowerCase()),
      };

      composeSearchParams = Object.entries<string>(composeSearchParams)
        .filter(([_, value]) => !!value)
        .reduce((accumulator: SearchParams<T>, [key, searchValue]) => {
          if (searchValue) {
            accumulator[key as keyof T] = searchValue;
          }

          return accumulator;
        }, {} as SearchParams<T>);

      const composeSearchHasValues = !!Object.values<string>(
        composeSearchParams,
      ).filter(searchValue => !!searchValue).length;

      setSearchParams(composeSearchParams);
      tableRef.current?.scrollTo(0, 0);

      if (onSearch) {
        onSearch(composeSearchParams);
        return;
      }

      const filteredRows = (data as RowType<T>[]).filter(row => {
        const composeRow = Object.entries<string>(
          row as unknown as Record<string, string>,
        )
          .filter(([_, value]) => !!value)
          .map(([key, value]) => [
            key,
            typeof value === 'string' ? value : JSON.stringify(value),
          ]);

        const searchParamsLength = Object.keys(composeSearchParams).length;
        const contains = !!(
          composeRow.filter(([key, value]) =>
            value.toLowerCase().includes(composeSearchParams[key as keyof T]),
          ).length === searchParamsLength
        );

        return contains;
      });

      setRows(composeSearchHasValues ? filteredRows : (data as RowType<T>[]));
    },
    [data, onSearch, searchParams],
  );

  const defaultCompareFunction = useCallback(
    (orderParam: Order, orderByParam: keyof T) => {
      if (columnCompareFunction) {
        const compareFunction = columnCompareFunction(orderParam, orderByParam);

        return compareFunction ?? getComparator(orderParam, orderByParam);
      }

      return getComparator(orderParam, orderByParam);
    },
    [columnCompareFunction],
  );

  const memoSortedRows = useMemo(() => {
    return stableSort(rows, defaultCompareFunction(order, orderBy as keyof T));
  }, [rows, defaultCompareFunction, order, orderBy]);

  const handlePagination = useCallback(
    (newPage: number) => {
      setCurrentPage(newPage);

      if (onPagination) {
        onPagination(newPage);
      }

      tableRef.current?.scrollTo(0, 0);
    },
    [onPagination],
  );

  useEffect(() => {
    if (data && data.length) {
      setRows(data as RowType<T>[]);

      if (data.length === 1) {
        setExpanded({ [(data as RowType<T>[])[0].id]: true });
      }
    }
  }, [data]);

  useEffect(() => {
    if (loading) {
      setExpanded(() => ({}));
    } else {
      setExpanded(() => {
        if (rows.length === 1) {
          const [row] = rows;
          return { [row.id]: true };
        }

        return {};
      });
    }
  }, [loading, rows]);

  useEffect(() => {
    setRows(data as RowType<T>[]);
  }, [data]);

  if (!loading && !data.length && !memoHasSearchParams) {
    return <NoData showSubtitle={false} />;
  }

  return (
    <div className={classes.container}>
      <div
        className={classes.root}
        style={{ height: data?.length > 1 ? height : undefined }}
        ref={tableRef}
      >
        {pagination &&
          (paginationPosition === 'both' || paginationPosition === 'top') &&
          data.length > 1 && (
            <Pagination
              {...pagination}
              limit={limit}
              loading={loading}
              onPageChange={handlePagination}
              description={paginationDescription}
              alignment={paginationAlignment}
              position={paginationPosition}
            />
          )}
        <Head
          pagination={pagination}
          fixedHeader={fixedHeader}
          searchParams={searchParams}
          loading={loading}
          onSort={handleColumnSort}
          onSearch={handleSearch}
          order={order}
          orderBy={orderBy}
          expansible={expansible}
          defineColumns={memoDefineColumns}
          loadingColumnsQuantity={loadingColumnsQuantity}
        />
        <Box style={{ height: 46 }} />
        <div
          ref={tableRef}
          className={clsx(classes.rowsContainer, {
            [classes.loading]: loading,
          })}
          style={{
            paddingLeft: loading ? 4 : undefined,
            height: data?.length > 1 ? `calc(${height} - 56px)` : 260,
            maxHeight: data?.length > 1 ? `calc(${height} - 56px)` : undefined,
          }}
        >
          {loading &&
            memoLoadingRowsQuantity.map(rowNumber => (
              <Fragment key={rowNumber}>
                <Skeleton animation="wave" className={classes.skeleton} />{' '}
              </Fragment>
            ))}
          {!loading &&
            !!memoSortedRows.length &&
            memoSortedRows.map((row, index) => (
              <Row
                key={row.id}
                row={row}
                index={index}
                onRowClick={handleSelectRow}
                defineColumns={memoDefineColumns}
                rows={rows}
                context={context}
                expanded={expanded}
                expansible={expansible}
                onExpand={handleExpand}
                selectable={rowSelectable}
                selectedRows={selectedRows}
                allExpanded={allExpanded}
              />
            ))}
          {!loading && !memoSortedRows.length && (
            <NoData
              title="Sem dados para a busca correspondente"
              showSubtitle={false}
            />
          )}
        </div>
      </div>
      {pagination &&
        (paginationPosition === 'both' || paginationPosition === 'bottom') &&
        data.length > 1 && (
          <Pagination
            {...pagination}
            limit={limit}
            loading={loading}
            onPageChange={handlePagination}
            description={paginationDescription}
            alignment={paginationAlignment}
            position={paginationPosition}
          />
        )}
    </div>
  );
}
